import { useState, useEffect } from "react";
import { Box, Divider, Grid, LoadingOverlay, Paper, Stack, Switch, Text, Title } from "@mantine/core";
import { useDebouncedCallback } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useGetNotificationPreferencesQuery, useUpdateNotificationPreferencesMutation } from "../../api/preferences.api";
import { NotificationPreferences } from "../../models/preferences.models";

export const NotificationsPreferences = () => {
	const { data: fetchedNotificationPreferences, isPending: isLoadingPreferences } = useGetNotificationPreferencesQuery();
	const { mutate: updateNotificationPreferences } = useUpdateNotificationPreferencesMutation();

	const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferences | null>(null);

	useEffect(() => {
		if (fetchedNotificationPreferences) {
			setNotificationPreferences(fetchedNotificationPreferences.NotificationPreferences);
		}
	}, [fetchedNotificationPreferences]);

	const debouncedUpdatePreferences = useDebouncedCallback((userId: string, prefs: NotificationPreferences) => {
		updateNotificationPreferences({
			UserId: userId,
			NotificationPreferences: prefs
		}, {
			onSuccess: () => {
				notifications.show({
					title: 'Preferences Updated',
					message: 'Your notification preferences have been updated successfully.',
					color: 'green',
					autoClose: 3000,
					radius: 'md'
				});
			},
			onError: () => {
				notifications.show({
					title: 'Update Failed',
					message: 'There was an error updating your notification preferences. Please try again.',
					color: 'red',
					autoClose: 5000,
					radius: 'md'
				});
			}
		});
	}, 1000);

	const handlePreferenceChanged = (key: keyof NotificationPreferences, value: boolean) => {
		// Update the preferences state or make an API call to save the changes
		console.log(`Preference ${key} changed to ${value}`);

		let updatedPreferences = { ...notificationPreferences, [key]: value };

		console.log(updatedPreferences);

		setNotificationPreferences({
				...notificationPreferences,
				...updatedPreferences
			} as NotificationPreferences);
		
		debouncedUpdatePreferences(fetchedNotificationPreferences?.User.UserId || "", updatedPreferences as NotificationPreferences);
	};
	return <>
		<Stack gap={0}>
			<Title order={2}>Notification Preferences</Title>
			<Text mt="xs">Set your notification preferences for various events.</Text>
		</Stack>

		<Paper withBorder p="lg" mt="md" radius="lg">
			<Title order={4}>Work Orders</Title>

			<Box pos="relative">
				<LoadingOverlay visible={isLoadingPreferences} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
				<Grid my="md" gutter="md">
					<Grid.Col span={{ base: 12, sm: 4 }}>
						<Text fw="bold">Created by me</Text>
						<Stack mt="md">
							<Switch label="All new comments" checked={notificationPreferences?.WorkOrderCreatedByMeNewComments} onChange={(event) => handlePreferenceChanged("WorkOrderCreatedByMeNewComments", event.currentTarget.checked)} w="fit-content" />
							<Switch label="Becomes overdue" checked={notificationPreferences?.WorkOrderCreatedByMeOverdue} onChange={(event) => handlePreferenceChanged("WorkOrderCreatedByMeOverdue", event.currentTarget.checked)} w="fit-content" />
							<Switch label="Status has changed" checked={notificationPreferences?.WorkOrderCreatedByMeStatusUpdated} onChange={(event) => handlePreferenceChanged("WorkOrderCreatedByMeStatusUpdated", event.currentTarget.checked)} w="fit-content" />
							<Switch label="Completed" checked={notificationPreferences?.WorkOrderCompleted} onChange={(event) => handlePreferenceChanged("WorkOrderCompleted", event.currentTarget.checked)} w="fit-content" />
							<Switch label="Archived" checked={notificationPreferences?.WorkOrderArchived} onChange={(event) => handlePreferenceChanged("WorkOrderArchived", event.currentTarget.checked)} w="fit-content" />
						</Stack>
					</Grid.Col>
					<Grid.Col span={{ base: 12, sm: 4 }}>
						<Text fw="bold">Assigned to me</Text>
						<Stack mt="md">
							<Switch label="All assigned work orders" checked={notificationPreferences?.WorkOrderAssignedToMe} onChange={(event) => handlePreferenceChanged("WorkOrderAssignedToMe", event.currentTarget.checked)} w="fit-content" />
							<Switch label="All new comments" checked={notificationPreferences?.WorkOrderAssignedToMeNewComments} onChange={(event) => handlePreferenceChanged("WorkOrderAssignedToMeNewComments", event.currentTarget.checked)} w="fit-content" />
							<Switch label="Becomes overdue" checked={notificationPreferences?.WorkOrderAssignedToMeOverdue} onChange={(event) => handlePreferenceChanged("WorkOrderAssignedToMeOverdue", event.currentTarget.checked)} w="fit-content" />
							<Switch label="Status has changed" checked={notificationPreferences?.WorkOrderAssignedToMeStatusUpdated} onChange={(event) => handlePreferenceChanged("WorkOrderAssignedToMeStatusUpdated", event.currentTarget.checked)} w="fit-content" />
							<Switch label="Completed" checked={notificationPreferences?.WorkOrderAssignedToMeCompleted} onChange={(event) => handlePreferenceChanged("WorkOrderAssignedToMeCompleted", event.currentTarget.checked)} w="fit-content" />
							<Switch label="Archived" checked={notificationPreferences?.WorkOrderAssignedToMeArchived} onChange={(event) => handlePreferenceChanged("WorkOrderAssignedToMeArchived", event.currentTarget.checked)} w="fit-content" />
						</Stack>
					</Grid.Col>
					<Grid.Col span={{ base: 12, sm: 4 }}>
						<Text fw="bold">Assigned to my team</Text>
						<Stack mt="md">
							<Switch label="All assigned work orders" checked={notificationPreferences?.WorkOrderAssignedToTeam} onChange={(event) => handlePreferenceChanged("WorkOrderAssignedToTeam", event.currentTarget.checked)} w="fit-content" />
							<Switch label="All new comments" checked={notificationPreferences?.WorkOrderAssignedToTeamNewComments} onChange={(event) => handlePreferenceChanged("WorkOrderAssignedToTeamNewComments", event.currentTarget.checked)} w="fit-content" />
							<Switch label="Becomes overdue" checked={notificationPreferences?.WorkOrderAssignedToTeamOverdue} onChange={(event) => handlePreferenceChanged("WorkOrderAssignedToTeamOverdue", event.currentTarget.checked)} w="fit-content" />
							<Switch label="Status has changed" checked={notificationPreferences?.WorkOrderAssignedToTeamStatusUpdated} onChange={(event) => handlePreferenceChanged("WorkOrderAssignedToTeamStatusUpdated", event.currentTarget.checked)} w="fit-content" />
							<Switch label="Completed" checked={notificationPreferences?.WorkOrderAssignedToTeamCompleted} onChange={(event) => handlePreferenceChanged("WorkOrderAssignedToTeamCompleted", event.currentTarget.checked)} w="fit-content" />
							<Switch label="Archived" checked={notificationPreferences?.WorkOrderAssignedToTeamArchived} onChange={(event) => handlePreferenceChanged("WorkOrderAssignedToTeamArchived", event.currentTarget.checked)} w="fit-content" />
						</Stack>
					</Grid.Col>
				</Grid>
			</Box>
			
			<Divider my="xl" />

			<Title order={4}>Requests</Title>

			<Box pos="relative">
				<LoadingOverlay visible={isLoadingPreferences} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
				<Grid my="md" gutter="md">
					<Grid.Col span={{ base: 12, sm: 4 }}>
						<Text fw="bold">New Requests</Text>
						<Stack mt="md">
							<Switch label="New Request Submitted" checked={notificationPreferences?.WorkOrderRequested} onChange={(event) => handlePreferenceChanged("WorkOrderRequested", event.currentTarget.checked)} w="fit-content" />
						</Stack>
					</Grid.Col>
					<Grid.Col span={{ base: 12, sm: 4 }}>
						<Text fw="bold">Created by me</Text>
						<Stack mt="md">
							<Switch label="Approved" checked={notificationPreferences?.WorkOrderApproved} onChange={(event) => handlePreferenceChanged("WorkOrderApproved", event.currentTarget.checked)} w="fit-content" />
							<Switch label="Declined" checked={notificationPreferences?.WorkOrderDeclined} onChange={(event) => handlePreferenceChanged("WorkOrderDeclined", event.currentTarget.checked)} w="fit-content" />
						</Stack>
					</Grid.Col>
				</Grid>
			</Box>
		</Paper>
	</>
}